import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { tagOperations, usersOperations } from '@ardoq/core';
import {
  ColumnDefinitionArdoqMetadata,
  ExtendedRowNode,
  InventoryColumnDefinitionWithSelectionState,
  InventoryContext,
  InventoryReferenceRepresentation,
  ReferenceColumnDefinitionArdoqMetadata,
  ReferenceColumnDirection,
} from './types';
import { columnOperations } from './columns/columnOperations';
import { cellRendererPropsOperations } from './CellRenderers/cellRendererPropsOperations';
import {
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterFunc,
  ValueSetterFunc,
  ValueParserFunc,
  ValueGetterParams,
  IRowNode,
} from 'ag-grid-enterprise';
import {
  APIComponentAttributes,
  ArdoqId,
  ExternalDocumentProvider,
  NUMBER_FORMAT_PERCENTAGE,
  PersistedExternalDocument,
  type Provenance,
} from '@ardoq/api-types';
import { Maybe } from '@ardoq/common-helpers';
import { percentageEditValueToStoredValue } from '@ardoq/locale';
import { EnhancedScopeData } from '@ardoq/data-model';
import { isValidArdoqId } from '@ardoq/validation';
import { isEqual } from 'lodash';
import { toInventoryReferenceRepresentation } from './toInventoryReferenceRepresentation';

type ValueFormatter<T> = ValueFormatterFunc<APIComponentAttributes, T>;
type ValueParser<T> = ValueParserFunc<APIComponentAttributes, T>;

const getValue = ({
  value,
}: ValueFormatterParams<APIComponentAttributes, any>) => value ?? null;

const SelectMultipleListValueFormatter: ValueFormatter<string[]> = params => {
  const value = getValue(params);
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return value;
};

const ProvenanceValueFormatter: ValueFormatter<Provenance> = ({ value }) =>
  value?.source ?? '';

const SelectMultipleListValueParser: ValueParser<string[]> = params => {
  const value = params.newValue;

  if (!value || value === '') {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return value
    .split(',')
    .map(s => s.trim())
    .filter(s => s !== '');
};

const TagsValueParser: ValueParserFunc<
  APIComponentAttributes,
  string[],
  InventoryContext
> = params => {
  const value = params.newValue;

  if (!value || value === '') {
    return [];
  }

  const names = Array.isArray(value)
    ? value
    : tagOperations.parseTagInput(value);

  const workspaceId = params.data?.rootWorkspace;
  const existingTagNames = workspaceId
    ? (params.context.tagsByWorkspaceId[workspaceId] ?? []).map(tag => tag.name)
    : [];

  return tagOperations.canonicalizeTagNames(names, existingTagNames);
};

const DateRangeValueFormatter: ValueFormatter<{
  start: Maybe<string>;
  end: Maybe<string>;
}> = params => {
  const value = getValue(params);
  return value ? `${value.start ?? ''} - ${value.end ?? ''}` : '';
};

const DateRangeValueParser: ValueParser<{
  start: Maybe<string>;
  end: Maybe<string>;
}> = params => {
  const value = params.newValue;

  if (!value || value === '') {
    return { start: null, end: null };
  }

  const parts = value.split(' - ').map(s => s.trim());
  return {
    start: parts[0] && parts[0] !== '' ? parts[0] : null,
    end: parts[1] && parts[1] !== '' ? parts[1] : null,
  };
};

const CheckBoxValueParser: ValueParser<boolean | string> = params => {
  const value = params.newValue;

  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return value;
};

const NumberValueParser: ValueParser<number> = params => {
  const { colDef, context } = params;
  const value = params.newValue;

  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) {
    return null;
  }

  // Check if this field uses percentage formatting
  const field = enhancedScopeDataOperations.getFieldByName(
    context.scopeData,
    colDef.field!
  );
  const formatOption = field?.numberFormatOptions;
  const isPercentage = formatOption === NUMBER_FORMAT_PERCENTAGE;

  // Convert edit value (25) back to stored value (0.25) for percentages
  return isPercentage
    ? percentageEditValueToStoredValue(numericValue)
    : numericValue;
};

const UserValueFormatter: ValueFormatterFunc<
  APIComponentAttributes,
  ArdoqId,
  InventoryContext
> = params => {
  const userId = params.value;
  if (!userId) {
    return '';
  }

  if (cellRendererPropsOperations.isConfidentialFieldUserCannotSee(params)) {
    return '';
  }

  const user = params.context.users.find(user => user._id === userId) ?? null;
  return user ? usersOperations.getUserDisplayText(user) : String(userId);
};

const UserValueParser: ValueParserFunc<
  APIComponentAttributes,
  ArdoqId,
  InventoryContext
> = params => {
  const value = params.newValue;

  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  const users = params.context.users;

  const userById = users.find(user => user._id === trimmed);
  if (userById) {
    return userById._id;
  }

  const emailMatch = trimmed.match(/\(([^)]+)\)\s*$/);
  const email = (emailMatch ? emailMatch[1].trim() : trimmed).toLowerCase();
  const userByEmail = users.find(user => user.email?.toLowerCase() === email);
  if (userByEmail) {
    return userByEmail._id;
  }

  return value;
};

/**
 * Value parser and formatter pair for data serialization for non-primitive types.
 * Used by AG Grid for copy+paste operations, fill handle, and other data transfer scenarios.
 */
export const toValueFormatter = (
  column: InventoryColumnDefinitionWithSelectionState
) => {
  if (column.isDateRangeColumn) {
    return DateRangeValueFormatter;
  } else if (columnOperations.isSelectMultipleListColumn(column)) {
    return SelectMultipleListValueFormatter;
  } else if (columnOperations.isUserColumn(column)) {
    return UserValueFormatter;
  } else if (columnOperations.isReferenceColumn(column)) {
    return ReferenceValueFormatter;
  } else if (columnOperations.isParentColumn(column)) {
    return ParentValueFormatter;
  } else if (columnOperations.isLucidchartColumn(column)) {
    return LucidchartValueFormatter;
  } else if (columnOperations.isNameColumn(column)) {
    return NameValueFormatter;
  } else if (columnOperations.isProvenanceColumn(column)) {
    return ProvenanceValueFormatter;
  }
};

/**
 * Value parser and formatter pair for data serialization for non-primitive types.
 * Used by AG Grid for copy+paste operations, fill handle, and other data transfer scenarios.
 */
/**
 * Takes the ids out of `Name (id), Name (id)` as the copy writes it, and
 * equally out of bare ids pasted from anywhere else. A name cannot be mistaken
 * for one: `isValidArdoqId` wants 24 hex characters.
 */
const toComponentIdsFromClipboardText = (text: string): ArdoqId[] =>
  (text.match(/[a-f0-9]{24}/g) ?? []).filter(isValidArdoqId);

/**
 * Text holding no id names its components instead, comma separated, the way a
 * copied cell writes them. A component whose own name holds a comma can only
 * be pasted by id.
 */
export const toComponentNamesFromClipboardText = (text: string): string[] =>
  text
    .split(',')
    .map(name => name.trim())
    .filter(name => name !== '');

/**
 * A name cannot be resolved while the paste runs, so it is left on the row for
 * the flush to look up. AG Grid calls the parser once per pasted cell, which is
 * the only place the clipboard text for that cell can be seen.
 */
const rememberPastedNames = (
  node: IRowNode<APIComponentAttributes> | null,
  field: string | undefined,
  names: string[]
) => {
  if (!node || !field || names.length === 0) {
    return;
  }
  const extendedNode = node as ExtendedRowNode;
  extendedNode._pendingComponentNames = {
    ...extendedNode._pendingComponentNames,
    [field]: names,
  };
};

/**
 * Paste re-points the copied cell at the row it lands on: the row's own
 * component becomes one end and the copied component the other. The reference
 * type is resolved against whichever model owns the reference, which for an
 * incoming column is the pasted component's — not necessarily loaded here, so
 * it may be left for the flush to resolve once fetched.
 */
const ReferenceValueParser: ValueParserFunc<
  APIComponentAttributes,
  InventoryReferenceRepresentation[],
  InventoryContext
> = params => {
  const text = typeof params.newValue === 'string' ? params.newValue : '';
  if (text.trim() === '') {
    return null;
  }

  const direction = columnOperations.getAgGridReferenceColumnDirection(
    params.colDef
  );
  const targetComponent = params.data;
  if (!direction || !targetComponent) {
    return params.oldValue;
  }

  const componentIds = toComponentIdsFromClipboardText(text);
  if (componentIds.length === 0) {
    rememberPastedNames(
      params.node,
      params.colDef.field,
      toComponentNamesFromClipboardText(text)
    );
    return params.oldValue;
  }

  const { referenceTypeName } = params.colDef
    .context as ReferenceColumnDefinitionArdoqMetadata;

  const parsedReferences = componentIds
    .filter(componentId => componentId !== targetComponent._id)
    .map(referencedComponentId =>
      toInventoryReferenceRepresentation({
        scopeData: params.context.scopeData,
        editedComponent: targetComponent,
        referencedComponentId,
        direction,
        referenceTypeName,
      })
    );

  return parsedReferences.length > 0 ? parsedReferences : params.oldValue;
};

export const toValueParser = (
  column: InventoryColumnDefinitionWithSelectionState
) => {
  if (column.isDateRangeColumn) {
    return DateRangeValueParser;
  }
  if (columnOperations.isCheckBoxColumn(column)) {
    return CheckBoxValueParser;
  }
  if (columnOperations.isTagsColumn(column)) {
    return TagsValueParser;
  }
  if (columnOperations.isSelectMultipleListColumn(column)) {
    return SelectMultipleListValueParser;
  }
  if (columnOperations.isNumberColumn(column)) {
    return NumberValueParser;
  }
  if (columnOperations.isUserColumn(column)) {
    return UserValueParser;
  }
  if (columnOperations.isParentColumn(column)) {
    return ParentValueParser;
  }
  if (columnOperations.isReferenceColumn(column)) {
    return ReferenceValueParser;
  }
  if (columnOperations.isLucidchartColumn(column)) {
    return LucidchartValueParser;
  }
  if (columnOperations.isNameColumn(column)) {
    return NameValueParser;
  }
};

const toProvisionalDocument = (
  documentId: string,
  url: string
): PersistedExternalDocument => ({
  _id: documentId,
  componentId: '',
  externalId: documentId,
  url,
  displayText: documentId,
  provider: ExternalDocumentProvider.LUCIDCHART,
  created: '',
  createdBy: '',
  createdByEmail: '',
  createdByName: '',
  lastModifiedBy: '',
  lastModifiedByEmail: '',
  lastModifiedByName: '',
  lastUpdated: '',
});

const LucidchartValueParser: ValueParserFunc<
  APIComponentAttributes,
  PersistedExternalDocument[],
  InventoryContext
> = params => {
  const text = typeof params.newValue === 'string' ? params.newValue : '';
  if (text.trim() === '') {
    return [];
  }

  const componentId = params.data?._id;
  const { toLucidchartDocumentIds, getLucidchartDocumentUrl } =
    params.context.commands;
  const documentIds = toLucidchartDocumentIds(text);
  if (!componentId || documentIds.length === 0) {
    return params.oldValue;
  }

  return documentIds.map(documentId => ({
    ...toProvisionalDocument(documentId, getLucidchartDocumentUrl(documentId)),
    componentId,
  }));
};

const LucidchartValueSetter: ValueSetterFunc<
  APIComponentAttributes,
  PersistedExternalDocument[],
  InventoryContext
> = params => !isEqual(params.oldValue ?? [], params.newValue ?? []);

const TypeValueGetter: ValueGetterFunc<
  APIComponentAttributes,
  string
> = params => params.data?.typeId ?? null;

const LucidchartValueGetter: ValueGetterFunc<
  APIComponentAttributes,
  PersistedExternalDocument[],
  InventoryContext
> = params => {
  const componentId = params.data?._id;
  if (!componentId) return [];
  const documents = params.context.externalDocumentsByComponentId[componentId];
  if (!documents) return [];
  return documents.filter(
    doc => doc.provider === ExternalDocumentProvider.LUCIDCHART
  );
};

const getReferenceValueGetter = (): ValueGetterFunc<
  APIComponentAttributes,
  InventoryReferenceRepresentation[],
  InventoryContext
> => {
  return (
    params: ValueGetterParams<
      APIComponentAttributes,
      InventoryReferenceRepresentation[],
      InventoryContext
    >
  ) => {
    const colDef = params.colDef;
    const columnMetadata = colDef.context as ColumnDefinitionArdoqMetadata;
    const component = params.data;
    const currentComponentId = component?._id;

    if (
      columnMetadata.inventoryColumnType !== 'reference' ||
      !currentComponentId ||
      !colDef.field ||
      !component
    ) {
      return [];
    }

    const scopeData = params.context.scopeData;
    const direction = columnMetadata.direction;
    const referencedComponentIds: ArdoqId[] =
      (component.derivedData?.[colDef.field] as ArdoqId[]) ?? [];

    if (direction === 'outgoing') {
      // for outgoing references we can just use the derivedData field
      return referencedComponentIds.map(referencedComponentId => {
        const sourceComponentModelId = component.model;
        return {
          source: currentComponentId,
          target: referencedComponentId,
          type: enhancedScopeDataOperations.getReferenceTypeIdBySourceModelIdAndReferenceTypeName(
            scopeData,
            sourceComponentModelId,
            columnMetadata.referenceTypeName
          )!,
          direction,
        };
      });
    }

    return referencedComponentIds.map(referencedComponentId => {
      const type =
        enhancedScopeDataOperations.getReferenceTypeIdBySourceComponentId(
          scopeData,
          referencedComponentId,
          columnMetadata.referenceTypeName
        ); // it's possible that we don't find a type here if the reference is being created - this will show "Loading..." until data is refreshed

      return {
        source: referencedComponentId,
        target: currentComponentId,
        type: type!,
        direction,
      };
    });
  };
};

export const toValueGetter = (
  column: InventoryColumnDefinitionWithSelectionState
) => {
  if (columnOperations.isTypeColumn(column)) {
    return TypeValueGetter;
  }
  if (columnOperations.isReferenceColumn(column)) {
    return getReferenceValueGetter();
  }
  if (columnOperations.isLucidchartColumn(column)) {
    return LucidchartValueGetter;
  }
};

const TypeValueSetter: ValueSetterFunc<
  APIComponentAttributes,
  string
> = params => {
  if (typeof params.newValue === 'string') {
    params.data.typeId = params.newValue;
    return true;
  }
  return false;
};

const ReferenceValueSetter: ValueSetterFunc<
  APIComponentAttributes,
  (InventoryReferenceRepresentation | ArdoqId)[],
  InventoryContext
> = (params): boolean => {
  if (!params.colDef.field) {
    return false;
  }
  if (params.newValue === null || params.newValue === undefined) {
    params.data.derivedData = {
      ...(params.data.derivedData || {}),
      [params.colDef.field]: [],
    };
    return true;
  }

  if (Array.isArray(params.newValue)) {
    const referencedComponentIds: ArdoqId[] = params.newValue.map(item => {
      if (typeof item === 'string') {
        return item;
      }
      return item.direction === 'outgoing' ? item.target : item.source;
    });

    params.data.derivedData = {
      ...(params.data.derivedData || {}),
      [params.colDef.field]: referencedComponentIds,
    };
    return true;
  }
  return false;
};

export const toValueSetter = (
  column: InventoryColumnDefinitionWithSelectionState
) => {
  if (columnOperations.isTypeColumn(column)) {
    return TypeValueSetter;
  }
  if (columnOperations.isReferenceColumn(column)) {
    return ReferenceValueSetter;
  }
  if (columnOperations.isLucidchartColumn(column)) {
    return LucidchartValueSetter;
  }
};

/**
 * `Name (id)`, matching how the user column serialises `Name (email)`. Names
 * are not unique and may contain the separator, so the id is what a paste
 * resolves against; the name is there to keep the text readable elsewhere.
 */
const toNameWithIdText = (
  name: string | null | undefined,
  componentId: ArdoqId
): string => (name ? `${name} (${componentId})` : `(${componentId})`);

const toComponentClipboardText = (
  componentId: ArdoqId,
  scopeData: EnhancedScopeData
): string =>
  toNameWithIdText(scopeData.componentsById[componentId]?.name, componentId);

export const toComponentNameFromClipboardText = (text: string): string => {
  const [, name, id] = text.match(/^(.*?)\s*\(([^)]*)\)\s*$/) ?? [];
  return name !== undefined && isValidArdoqId(id.trim()) ? name : text;
};

export const toClipboardCellText = (text: string): string =>
  /[\t\r\n]/.test(text) || text.startsWith('"')
    ? `"${text.replaceAll('"', '""')}"`
    : text;

export const fromClipboardCellText = (text: string): string => {
  if (text.length < 2 || !text.startsWith('"') || !text.endsWith('"')) {
    return text;
  }
  const unquoted = text.slice(1, -1).replaceAll('""', '"');
  return toClipboardCellText(unquoted) === text ? unquoted : text;
};

const toComponentIdFromClipboardText = (text: string): Maybe<ArdoqId> => {
  const trimmed = text.trim();
  if (trimmed === '') {
    return null;
  }
  const idInParentheses = trimmed.match(/^[^,]*\(([^)]+)\)$/)?.[1].trim();
  return idInParentheses && isValidArdoqId(idInParentheses)
    ? idInParentheses
    : trimmed;
};

export const toReferenceClipboardText = (
  references: InventoryReferenceRepresentation[] | null | undefined,
  direction: ReferenceColumnDirection,
  scopeData: EnhancedScopeData
): string =>
  (references ?? [])
    .map(reference =>
      direction === 'outgoing' ? reference.target : reference.source
    )
    .map(componentId => toComponentClipboardText(componentId, scopeData))
    .join(', ');

const ReferenceValueFormatter: ValueFormatterFunc<
  APIComponentAttributes,
  InventoryReferenceRepresentation[],
  InventoryContext
> = params => {
  const direction = columnOperations.getAgGridReferenceColumnDirection(
    params.colDef
  );
  if (!direction) {
    return '';
  }
  return toReferenceClipboardText(
    params.value,
    direction,
    params.context.scopeData
  );
};

const toParentClipboardText = (
  parentId: ArdoqId | null | undefined,
  scopeData: EnhancedScopeData
): string => (parentId ? toComponentClipboardText(parentId, scopeData) : '');

const ParentValueFormatter: ValueFormatterFunc<
  APIComponentAttributes,
  ArdoqId,
  InventoryContext
> = params => toParentClipboardText(params.value, params.context.scopeData);

const ParentValueParser: ValueParserFunc<
  APIComponentAttributes,
  ArdoqId,
  InventoryContext
> = params => {
  const value = params.newValue;

  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value !== 'string') {
    return value;
  }

  const parsed = toComponentIdFromClipboardText(value);
  if (!parsed || isValidArdoqId(parsed) || !isValidArdoqId(params.data?._id)) {
    return parsed;
  }
  rememberPastedNames(params.node, params.colDef.field, [parsed]);
  return params.oldValue;
};

const NameValueFormatter: ValueFormatterFunc<
  APIComponentAttributes,
  string,
  InventoryContext
> = params => {
  const componentId = params.data?._id;
  if (!componentId || !isValidArdoqId(componentId)) {
    return params.value ?? '';
  }
  return toNameWithIdText(params.value, componentId);
};

const NameValueParser: ValueParserFunc<
  APIComponentAttributes,
  string,
  InventoryContext
> = params =>
  typeof params.newValue === 'string'
    ? toComponentNameFromClipboardText(params.newValue)
    : params.newValue;

const LucidchartValueFormatter: ValueFormatterFunc<
  APIComponentAttributes,
  PersistedExternalDocument[]
> = params => (params.value ?? []).map(document => document.url).join(', ');
